import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, X } from 'lucide-react';
import { Button } from '../ui/button-simple';

/** Trap focus inside a modal dialog */
function useFocusTrap(ref, isOpen) {
  useEffect(() => {
    if (!isOpen || !ref.current) return;
    const modal = ref.current;
    const focusable = modal.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length) focusable[0].focus();

    const handleTab = (e) => {
      if (e.key !== 'Tab' || !focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    modal.addEventListener('keydown', handleTab);
    return () => modal.removeEventListener('keydown', handleTab);
  }, [isOpen, ref]);
}

export const ConfirmDeleteModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  itemName,
  isLoading = false 
}) => {
  const modalRef = useRef(null);
  useFocusTrap(modalRef, isOpen);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && !isLoading) onClose();
  }, [onClose, isLoading]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-delete-title"
      ref={modalRef}
    >
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h3 id="confirm-delete-title" className="text-lg font-semibold text-gray-900">
              {title || 'Confirm Deletion'}
            </h3>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="mb-6">
          <p className="text-gray-600 mb-2">
            {message || 'Are you sure you want to delete this item?'}
          </p>
          {itemName && (
            <div className="p-3 bg-gray-50 rounded-lg border-l-4 border-red-500">
              <p className="font-medium text-gray-900">{itemName}</p>
            </div>
          )}
          <p className="text-sm text-red-600 mt-3">
            This action cannot be undone.
          </p>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <Button
            onClick={onClose}
            disabled={isLoading}
            variant="outline"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isLoading}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Deleting...</span>
              </div>
            ) : (
              'Delete'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export const StatusUpdateModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  currentStatus,
  itemName,
  isLoading = false 
}) => {
  const [selectedStatus, setSelectedStatus] = React.useState(currentStatus || 'Active');
  const modalRef = useRef(null);
  useFocusTrap(modalRef, isOpen);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && !isLoading) onClose();
  }, [onClose, isLoading]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  const statusOptions = [
    { value: 'Active', label: 'Active', color: 'text-green-700', bg: 'bg-green-100' },
    { value: 'Inactive', label: 'Inactive', color: 'text-red-700', bg: 'bg-red-100' },
    { value: 'Seasonal', label: 'Seasonal', color: 'text-orange-700', bg: 'bg-orange-100' }
  ];

  const handleConfirm = () => {
    onConfirm(selectedStatus);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="status-update-title"
      ref={modalRef}
    >
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h3 id="status-update-title" className="text-lg font-semibold text-gray-900">
            Update Business Status
          </h3>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            Update the business status for:
          </p>
          {itemName && (
            <div className="p-3 bg-gray-50 rounded-lg border-l-4 border-amber-500 mb-4">
              <p className="font-medium text-gray-900">{itemName}</p>
            </div>
          )}

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Business Status
            </label>
            <div className="space-y-2">
              {statusOptions.map((option) => (
                <label key={option.value} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    value={option.value}
                    checked={selectedStatus === option.value}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="form-radio h-4 w-4 text-amber-600 focus:ring-amber-500"
                  />
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${option.bg} ${option.color}`}>
                    {option.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <Button
            onClick={onClose}
            disabled={isLoading}
            variant="outline"
            className="flex-1"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isLoading || selectedStatus === currentStatus}
            className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
          >
            {isLoading ? (
              <div className="flex items-center justify-center space-x-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>Updating...</span>
              </div>
            ) : (
              'Update Status'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

ConfirmDeleteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string,
  message: PropTypes.string,
  itemName: PropTypes.string,
  isLoading: PropTypes.bool,
};

StatusUpdateModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  currentStatus: PropTypes.string,
  isLoading: PropTypes.bool,
};